import { useEffect, useState } from 'react';
import { Clock, ExternalLink, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { sessionEndMessage, type SessionEnd } from '@/services/sessionPolicy';
import { installSessionSync, markReconnectIntent, maybeRefresh, noteActivity, reconnectUrl } from '@/services/session';
import styles from './SessionGuard.module.scss';

/** Passage régulier : le jeton est-il à renouveler ? (la règle elle-même est dans sessionPolicy) */
const CHECK_EVERY_MS = 60_000;

/**
 * Garde la session du panel pendant que la personne travaille, monté dans le layout :
 * - renouvelle le jeton avant son échéance tant que l'onglet est affiché et utilisé ;
 * - suit les autres onglets (déconnexion, reconnexion) ;
 * - quand la session est finie, le dit clairement dans une fenêtre, sans rien jeter de la page :
 *   la reconnexion se fait dans un nouvel onglet, puis la page reprend telle quelle.
 */
export function SessionGuard() {
  const sessionEnd = useAuthStore((s) => s.sessionEnd);

  useEffect(() => {
    const onActivity = () => noteActivity();
    // Revenir sur l'onglet compte comme une action : un jeton échu pendant l'absence se voit
    // tout de suite, avant que la personne ne recommence à travailler.
    const onBack = () => {
      if (document.visibilityState !== 'visible') return;
      noteActivity();
      void maybeRefresh();
    };
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity);
    window.addEventListener('wheel', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    const stopSync = installSessionSync();
    const timer = window.setInterval(() => void maybeRefresh(), CHECK_EVERY_MS);
    noteActivity();
    void maybeRefresh();
    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('wheel', onActivity);
      window.removeEventListener('touchstart', onActivity);
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
      stopSync();
      window.clearInterval(timer);
    };
  }, []);

  return sessionEnd ? <SessionEndedDialog end={sessionEnd} /> : null;
}

function SessionEndedDialog({ end }: { end: SessionEnd }) {
  // Clic sur « Se reconnecter » : l'onglet de connexion est ouvert, on attend son jeton.
  const [opened, setOpened] = useState(false);
  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="alertdialog" aria-modal="true" aria-labelledby="session-end-title"
        aria-describedby="session-end-text">
        <div className={styles.icon}><Clock size={22} /></div>
        <h2 id="session-end-title" className={styles.title}>
          {end === 'expired' ? 'Session expirée' : 'Session terminée'}
        </h2>
        <p id="session-end-text" className={styles.text}>{sessionEndMessage(end)}</p>
        <p className={styles.hint}>
          La connexion s'ouvre dans un nouvel onglet et cette page reste telle quelle. Une fois
          reconnecté, reviens ici : la fenêtre se ferme toute seule, et tu refais simplement ce qui
          a échoué (rien n'est parti).
        </p>
        {/* Un vrai lien : jamais bloqué comme une fenêtre surgissante. */}
        <a className={styles.primary} href={reconnectUrl()} target="_blank" rel="noopener noreferrer"
          onClick={() => { markReconnectIntent(); setOpened(true); }}>
          <ExternalLink size={15} /> Se reconnecter
        </a>
        {opened && (
          <p className={styles.waiting} role="status">
            <Loader2 size={14} className={styles.spin} /> En attente de ta connexion dans l'autre onglet…
          </p>
        )}
      </div>
    </div>
  );
}
